// Jetski Content
import TwentyFourHourIcon from 'assets/images/psp/twentyfour-hour.svg';
import Camera from 'assets/images/psp/camera.svg';
import Garage from 'assets/images/psp/garage.svg';
import PaperWork from 'assets/images/psp/paperwork.svg';
import Renovate from 'assets/images/icons/psp/renovating.svg';
import SaveMoney from 'assets/images/psp/save-money.svg';
import Six from 'assets/images/psp/six.svg';
import Summer from 'assets/images/psp/summer.svg';
import JetskiHero from 'assets/images/psp/hero/jet-ski-hero.webp';
import Featured1 from 'assets/images/psp/FeaturedImages/jetSkiFeatured1.webp';
import Featured2 from 'assets/images/psp/FeaturedImages/kayakFeatured2.webp';
import Featured3 from 'assets/images/psp/FeaturedImages/jetSkiFeatured3.webp';

const jetSkiStorageContent =
	'## Types of Jet Ski Storage Options\n\n' +
	'### Indoor Jet Ski Storage\n\n' +
	'Indoor storage is the most popular choice for jet ski owners who want maximum protection for their investment. These facilities offer advanced protection from weather, theft, and other potential risks that could lead to costly repairs or maintenance issues.\n\n' +
	'Many indoor facilities feature climate-controlled environments specifically designed for watercraft storage, with drive-up access and convenient amenities like 24/7 access to your PWC.\n\n' +
	'### Outdoor Jet Ski Storage\n\n' +
	'For budget-conscious riders or short-term storage needs, outdoor jet ski storage provides an affordable alternative. Similar to boat storage, you can keep your PWC in an open-air parking area. Some facilities offer covered spaces for additional sun and rain protection.\n\n' +
	'While this is a more affordable jet ski storage option, it requires extra winterization and preparation for long-term storage.\n\n' +
	'### Boat and Jet Ski Storage Combo\n\n' +
	'[Many facilities offer specialized boat and jet ski storage](https://www.sparefoot.com/blog/how-to-find-the-right-storage-facility-for-your-car-rv-or-boat) areas that can accommodate both types of watercraft. These larger spaces are perfect for families with multiple water toys or those who want to store their jet ski alongside their boat during the off-season.\n\n';
const jetSkiStoragePrepMarkdown =
	'**Engine and Fuel System:** Start by running your jet ski to operating temperature, then add fuel stabilizer to prevent fuel degradation. For long-term storage, consider draining the fuel system completely or filling the tank with fresh fuel and stabilizer, then running the engine to circulate the treated fuel throughout the system.\n\n' +
	'**Cooling System Maintenance:** Flush the cooling system with fresh water to remove salt, sand, and debris that can cause corrosion. For jet ski water storage in areas with freezing temperatures, ensure all water is removed from the cooling system to prevent freeze damage.\n\n' +
	'**Battery Care:** Remove the battery and store it in a cool, dry place with a battery tender, or [connect a tender directly to the PWC](https://www.sparefoot.com/blog/proper-storage-tips-for-vehicle-batteries) if your storage unit has electrical outlets. This prevents sulfation and extends battery life during storage periods.\n\n' +
	'**Hull and Exterior Protection:** Clean the hull thoroughly and apply appropriate protectants to prevent UV damage and corrosion. A breathable watercraft cover helps prevent dust accumulation while allowing air circulation.';

const reviews = [
	{
		reviewTitle: 'Cheaper than booking directly.',
		link: 'https://www.trustpilot.com/reviews/556768550000ff0002e957f6',
		reviewRating: 5,
		reviewText:
			'We needed a place to store our jet skis. I was calling each storage facility directly. I called one storage facility that was very expensive. I was going to hang up when the representative told me of a facility closer to my home and the rate was cheaper. We booked the space and were happy when we arrived.',
		reviewerName: 'Debbie',
		reviewerInitials: 'D',
		reviewDate: '',
	},
	{
		reviewTitle: 'SpareFoot Website is Easy to Use',
		link: 'https://www.trustpilot.com/reviews/687915adb981a9b597b9949f',
		reviewRating: 5,
		reviewText:
			'The website allowed me to easily search for available storage units in the area I wanted. Then, once I found one it only took minutes to reserve one!',
		reviewerName: 'Robert Streich',
		reviewerInitials: 'RS',
		reviewDate: '',
	},
	{
		reviewTitle: 'One phone call was all that was needed!',
		link: 'https://www.trustpilot.com/reviews/6876983ecc24807789b58726',
		reviewRating: 5,
		reviewText:
			'One phone call was all that was needed. I spoke with a representative who took care on my needs in the most efficient manner. The process was quick and smooth.',
		reviewerName: 'Robert, South Carolina (USA)',
		reviewerInitials: 'R',
		reviewDate: '',
	},
	{
		reviewTitle: 'Easy to Use',
		link: 'https://www.trustpilot.com/reviews/6873aee374abbc40cb6c61b2',
		reviewRating: 5,
		reviewText:
			'I just typed in what I was looking for and a number of storage places popped up. The best part is I can make a reservation on this site without a credit card. That made the experience doable.',
		reviewerName: 'Colorado Customer',
		reviewerInitials: 'CC',
		reviewDate: '',
	},
];

const jetSkiFaqs = [
	{
		question: 'Can you store a jet ski in a storage unit?',
		answer:
			'Yes, you can store a jet ski in a storage unit. Storing your PWC in a self-storage unit is one of the smartest ways to protect your watercraft investment. Professional storage facilities regularly accommodate jet skis and other personal watercraft, offering both indoor and outdoor options to fit various budgets and protection needs.\n\n' +
			'Before renting, check with your chosen facility about their specific PWC policies. Most require proof of insurance and registration, and some may have requirements about fuel levels, battery disconnection, or cleaning requirements. The advantage of using **SpareFoot** is connecting you with facilities experienced in watercraft storage, ensuring you find a place that understands the unique needs of jet ski owners.',
	},
	{
		question: 'What size storage unit is required for a jet ski?',
		answer:
			'The size of the storage unit needed for your jet ski depends on your watercraft’s dimensions and how much additional gear you plan to store. Most standard jet skis measure **8–12 feet in length** and **3–4 feet in width**, making a **5x15 unit** the minimum recommended size for most PWCs.\n\n' +
			'For basic storage of a single jet ski, a 5x15 unit (5′ × 15′ × 8′) provides adequate space for most watercraft with minimal maneuvering room. However, we typically recommend a **10x10 unit** for better accessibility and gear storage. This size accommodates your jet ski plus life jackets, tow ropes, tools, and maintenance supplies.\n\n' +
			'If you’re storing multiple watercraft or want a complete maintenance area, consider a **10x15 or 10x20 unit**. These larger spaces work well for boat and jet ski storage combinations or for serious enthusiasts who want workshop space for repairs and seasonal preparation.\n\n' +
			'When determining what size storage unit is required for a jet ski, always measure your specific PWC and account for trailer dimensions if applicable. Remember to factor in space for walking around the unit and accessing your gear without cramping or potential damage to your watercraft.',
	},
	{
		question: 'How much does jet ski storage cost?',
		answer:
			'Jet ski storage cost varies significantly based on location, unit size, amenities, and seasonal demand. On average, you can expect to pay **$65–$275 per month** for indoor storage, with outdoor options ranging from **$75–$150 monthly**.\n\n' +
			'For standard indoor storage, a **5x15 unit** suitable for most jet skis costs approximately **$85 per month**, while **climate-controlled options** run about **$105 monthly**. Larger **10x10 units** average **$136** for standard indoor storage and **$165** for climate-controlled environments.\n\n' +
			'When budgeting for storage for a jet ski, consider additional costs like insurance requirements, facility fees, and seasonal rate fluctuations. Many facilities offer discounts for longer-term commitments or off-season storage, making annual contracts potentially more economical than month-to-month arrangements.',
	},
	{
		question: 'Should I choose indoor or outdoor jet ski storage?',
		answer:
			'The choice between indoor and outdoor jet ski storage depends on your budget, storage duration, and protection needs. **Indoor storage offers superior protection but costs more**, while **outdoor storage provides budget-friendly options** with basic security.\n\n' +
			'Choose indoor storage if you’re planning long-term storage, own a valuable or newer jet ski, live in areas with harsh weather conditions, or want maximum theft protection. **Climate-controlled indoor units** are ideal for jet ski winter storage, preventing freeze damage and corrosion while maintaining optimal humidity levels.\n\n' +
			'Indoor facilities typically offer better amenities like **electrical outlets for battery tenders**, **wash stations**, and **24/7 access**. They’re also better for maintenance work, providing clean, well-lit environments for repairs and seasonal preparation.',
	},
	{
		question: 'How do I prepare my jet ski for long-term storage?',
		answer:
			'Proper preparation is essential for successful long-term jet ski storage, preventing costly repairs and ensuring your PWC is ready for your next adventure. The process involves several critical steps that protect your engine, cooling system, and overall watercraft condition.\n\n' +
			'Start with engine preparation by running your jet ski to operating temperature, then adding fuel stabilizer to prevent fuel degradation. For extended storage, consider draining the fuel system completely or filling the tank with fresh fuel and stabilizer, then running the engine to circulate treated fuel throughout the system.\n\n' +
			'Cooling system maintenance is crucial, especially for jet ski water storage in freezing climates. Flush the cooling system thoroughly with fresh water to remove salt, sand, and debris that cause corrosion. Ensure all water is removed from the cooling system to prevent freeze damage.\n\n' +
			'Battery care involves removing the battery and storing it in a cool, dry place with a battery tender, or connecting a tender directly if your storage unit has electrical outlets. This prevents sulfation and extends battery life during storage periods.\n\n' +
			'Clean the hull thoroughly and apply appropriate protectants to prevent UV damage and corrosion. Use a breathable watercraft cover to prevent dust accumulation while allowing air circulation. Finally, document your preparation process and store all maintenance records with your jet ski for easy reference when retrieving your watercraft.',
	},
	{
		question:
			'What amenities should I look for in a jet ski storage facility?',
		answer:
			'When selecting a jet ski storage facility, certain amenities can significantly enhance your storage experience and better protect your watercraft investment. Look for facilities that offer features specifically beneficial for PWC storage and maintenance.\n\n' +
			'**Climate control** is valuable for long-term storage, maintaining consistent temperature and humidity levels that prevent corrosion and component degradation. While more expensive, climate-controlled units are worthwhile for valuable or vintage watercraft.\n\n' +
			'Practical amenities include **electrical outlets for battery tenders**, **wash stations** for cleaning before storage, and **drive-up access** for easy loading and unloading. Some facilities offer **maintenance areas** where you can perform repairs and seasonal preparation.\n\n' +
			'**Location convenience** matters significantly – choose facilities with easy highway access and proximity to your preferred boating areas. Some **marina storage facilities** offer direct water access, though these typically cost more and may have seasonal limitations.',
	},
];

export const JetskiContent = {
	type: 'Jet Ski',
	sections: [
		{
			title: 'Jetski Storage Hero',
			background: 'yellow',
			modules: [
				{
					type: 'hero',
					background: 'lighterYellow',
					props: {
						content: {
							title: 'Find the Best Jet Ski Storage For You',
							description:
								'Your jet ski represents pure aquatic freedom – those weekend escapes, spontaneous lake trips, and adrenaline-pumping adventures that make summer unforgettable. Proper jet ski storage is essential for protecting your investment and ensuring your water warrior stays ready for your next adventure. Professional jet ski storage offers the ideal solution, providing secure and accessible facilities that safeguard your personal watercraft (PWC) from harsh weather, theft, and seasonal damage.',
							image: JetskiHero,
							CTAText: 'Find Jet Ski Storage',
						},
					},
				},
			],
		},
		{
			title: 'nearby storage',
			noPaddingBottom: true,
			background: 'gray',
			modules: [
				{
					type: 'facilitiesNearYou',
					props: {
						title: 'Popular Facilities Near You',
					},
				},
			],
		},
		{
			title: 'Why You Should Store Your Jet Ski in a Storage Unit',
			modules: [
				{
					type: 'content',
					props: {
						title: 'Why You Should Store Your Jet Ski in a Storage Unit',
						contentType: 'row-type',
						ShowBackgroundImage: false,
						content: [
							"[Storing your jet ski in a storage facility](https://www.sparefoot.com/blog/how-to-store-your-jet-ski) keeps your watercraft secure and protected when you're not hopping over the waves. With convenient jet ski storage near you, accessing your stored PWC is easier than dealing with crowded garages or risky outdoor storage – plus you get the added security and climate protection that your driveway simply can't match.",
						],
					},
				},
				{
					type: 'sizeChart',
					props: {
						headers: [
							{
								label: 'Benefit',
								key: 'Benefit',
							},
							{
								label: 'Description',
								key: 'Description',
							},
						],
						rows: [
							{
								Benefit: {
									type: 'icon',
									icon: Six,
									heading: 'Weather Protection',
								},
								Description: {
									type: 'markdown',
									markdown:
										'[Climate-controlled units prevent corrosion](https://www.sparefoot.com/blog/do-you-need-climate-control-in-your-storage-unit), UV damage, and deterioration from rain, snow, and extreme temperatures, making jet ski winter storage a worry-free experience.',
								},
							},
							{
								Benefit: {
									type: 'icon',
									icon: Camera,
									heading: 'Enhanced Security',
								},
								Description:
									'Security systems, gated access, and surveillance cameras provide superior protection compared to home storage options.',
							},
							{
								Benefit: {
									type: 'icon',
									icon: Garage,
									heading: 'Extra Space',
								},
								Description:
									'Free up valuable garage and driveway space at home for cars, tools, and other essentials.',
							},
							{
								Benefit: {
									type: 'icon',
									icon: Summer,
									heading: 'Seasonal Convenience',
								},
								Description:
									'The perfect solution for riders in climates with distinct seasons who need secure storage for months at a time.',
							},
							{
								Benefit: {
									type: 'icon',
									icon: TwentyFourHourIcon,
									heading: 'Easy Access',
								},
								Description:
									'Convenient access when you want to ride, without having to move cars or clear garage clutter.',
							},
							{
								Benefit: {
									type: 'icon',
									icon: PaperWork,
									heading: 'Insurance Benefits',
								},
								Description:
									'Many insurance companies offer reduced premiums for jet skis stored in secure facilities.',
							},
							{
								Benefit: {
									type: 'icon',
									icon: Renovate,
									heading: 'Maintenance Friendly',
								},
								Description:
									'A clean, dry environment is ideal for performing maintenance, repairs, and seasonal prep work.',
							},
							{
								Benefit: {
									type: 'icon',
									icon: SaveMoney,
									heading: 'Cost Effective',
								},
								Description:
									'Often cheaper than building additional garage space or risking expensive weather and theft damage.',
							},
						],
					},
				},
			],
		},
		{
			title: 'Jet Ski Storage Unit Size Guide',
			noPaddingTop: true,
			modules: [
				{
					type: 'content',
					props: {
						title: 'Jet Ski Storage Unit Size Guide',
						contentType: 'row-type',
						ShowBackgroundImage: false,
						content: [
							"[Choosing the right storage unit size](https://www.sparefoot.com/storage-unit-size-guide.html) for your jet ski depends on your watercraft's dimensions, how much gear you plan to store, and whether you need space for maintenance. While most jet skis can fit in smaller units, having extra room for life jackets, tow ropes, tools, and easy maneuvering makes the storage experience much more convenient. Use this guide to find the perfect fit for your aquatic investment.",
						],
					},
				},
				{
					type: 'sizeChart',
					props: {
						headers: [
							{
								label: 'Unit Size',
								key: 'Unit Size',
							},
							{
								label: 'Dimensions',
								key: 'Dimensions',
							},
							{
								label: 'Best For',
								key: 'Best For',
							},
							{
								label: 'What Fits',
								key: 'What Fits',
							},
							{
								label: 'Average Monthly Cost',
								key: 'Average Monthly Cost',
							},
						],
						rows: [
							{
								'Unit Size': {
									type: 'markdown',
									markdown:
										'**[5x10](https://www.sparefoot.com/blog/5x10-storage-unit-size-guide)**',
								},
								Dimensions: "5' × 10' × 8'",
								'Best For': 'Compact PWCs only',
								'What Fits':
									'Small personal watercraft, minimal gear',
								'Average Monthly Cost': '$40 - $80',
							},
							{
								'Unit Size': {
									type: 'markdown',
									markdown: '**5x15**',
								},
								Dimensions: "5' × 15' × 8'",
								'Best For': 'Standard jet ski',
								'What Fits':
									'Most jet skis with basic accessories',
								'Average Monthly Cost': '$50 - $100',
							},
							{
								'Unit Size': {
									type: 'markdown',
									markdown:
										'**[10x10](https://www.sparefoot.com/blog/10x10-storage-unit-size-guide)**',
								},
								Dimensions: "10' × 10' × 8'",
								'Best For': 'Jet ski + gear',
								'What Fits':
									'One PWC plus life jackets, tow ropes, tools, and accessories',
								'Average Monthly Cost': '$70 - $140',
							},
							{
								'Unit Size': {
									type: 'markdown',
									markdown: '**10x15**',
								},
								Dimensions: "10' × 15' × 8'",
								'Best For': 'Large PWC or multiple units',
								'What Fits':
									'Luxury jet skis, or 2 smaller PWCs with gear',
								'Average Monthly Cost': '$90 - $180',
							},
							{
								'Unit Size': {
									type: 'markdown',
									markdown:
										'**[10x20](https://www.sparefoot.com/blog/10x20-storage-unit-size-guide)**',
								},
								Dimensions: "10' × 20' × 8'",
								'Best For': 'Serious water enthusiasts',
								'What Fits':
									'Multiple jet skis, complete maintenance setup, seasonal storage',
								'Average Monthly Cost': '$110 - $220',
							},
							{
								'Unit Size': {
									type: 'markdown',
									markdown: '**10x25**',
								},
								Dimensions: "10' × 25' × 8'",
								'Best For': 'Jet ski collection',
								'What Fits':
									'3–4 PWCs, boat and jet ski storage combo, workshop area',
								'Average Monthly Cost': '$130 - $260',
							},
						],
					},
				},
				{
					type: 'content',
					props: {
						contentType: 'row-type',
						ShowBackgroundImage: false,
						content: [
							'*Prices vary by location and amenities like climate control.*\n\n',
						],
					},
				},
			],
		},
		{
			title: 'Types of Jet Ski Storage Options',
			background: 'yellow',
			modules: [
				{
					type: 'content',
					props: {
						contentType: 'row-type',
						ShowBackgroundImage: true,
						content: [jetSkiStorageContent],
					},
				},
			],
		},
		{
			title: 'Types of Jet Ski Storage Options',
			modules: [
				{
					type: 'content',
					props: {
						title: 'Jet Ski Storage Costs (2025)',
						contentType: 'row-type',
						ShowBackgroundImage: false,
						content: [
							'Understanding jet ski storage costs helps you budget effectively for protecting your watercraft. Storage for jet skis varies depending on factors including unit size, location, amenities, seasonal demand, and security features.\n\n' +
								'### Indoor Jet Ski Storage Cost\n' +
								'Most jet ski owners choose indoor storage to balance protection and affordability, especially for valuable or newer watercraft. Storing your jet ski in a storage unit offers the perfect balance of peace of mind and affordability, often over other options like marina storage.',
						],
					},
				},
				{
					type: 'sizeChart',
					props: {
						headers: [
							{
								label: 'Unit Size',
								key: 'Unit Size',
							},
							{
								label: 'Full Dimensions',
								key: 'Full Dimensions',
							},
							{
								label: 'Average Monthly Cost',
								key: 'Average Monthly Cost',
							},
						],
						rows: [
							{
								'Unit Size': {
									type: 'markdown',
									markdown: '**5x10**',
								},
								'Full Dimensions': "5' × 10' × 8'",
								'Average Monthly Cost': '$40 - $80',
							},
							{
								'Unit Size': {
									type: 'markdown',
									markdown: '**5x15**',
								},
								'Full Dimensions': "5' × 15' × 8'",
								'Average Monthly Cost': '$50 - $100',
							},
							{
								'Unit Size': {
									type: 'markdown',
									markdown: '**10x10**',
								},
								'Full Dimensions': "10' × 10' × 8'",
								'Average Monthly Cost': '$70 - $140',
							},
							{
								'Unit Size': {
									type: 'markdown',
									markdown: '**10x15**',
								},
								'Full Dimensions': "10' × 15' × 8'",
								'Average Monthly Cost': '$90 - $180',
							},
							{
								'Unit Size': {
									type: 'markdown',
									markdown: '**10x20**',
								},
								'Full Dimensions': "10' × 20' × 8'",
								'Average Monthly Cost': '$110 - $220',
							},
							{
								'Unit Size': {
									type: 'markdown',
									markdown: '**10x25**',
								},
								'Full Dimensions': "10' × 25' × 8'",
								'Average Monthly Cost': '$130 - $260',
							},
						],
					},
				},
				{
					type: 'content',
					props: {
						contentType: 'row-type',
						ShowBackgroundImage: false,
						content: [
							'### Outdoor Jet Ski Storage Cost\n' +
								'Wondering how much jet ski storage is for outdoor options? These rates provide the most budget-friendly solution for short-term needs.',
						],
					},
				},
				{
					type: 'sizeChart',
					props: {
						headers: [
							{
								label: 'Storage Type',
								key: 'Storage Type',
							},
							{
								label: 'Average Monthly Cost',
								key: 'Average Monthly Cost',
							},
							{
								label: 'Price Range',
								key: 'Price Range',
							},
							{
								label: 'Protection Level',
								key: 'Protection Level',
							},
						],
						rows: [
							{
								'Storage Type': {
									type: 'markdown',
									markdown: '**Outdoor Uncovered**',
								},
								'Average Monthly Cost': '$75',
								'Price Range': '$40 - $120',
								'Protection Level': 'Basic security only',
							},
							{
								'Storage Type': {
									type: 'markdown',
									markdown: '**Outdoor Covered**',
								},
								'Average Monthly Cost': '$110',
								'Price Range': '$60 - $170',
								'Protection Level':
									'Weather protection from the sun/rain',
							},
							{
								'Storage Type': {
									type: 'markdown',
									markdown: '**Marina Storage**',
								},
								'Average Monthly Cost': '$150',
								'Price Range': '$80 - $250',
								'Protection Level':
									'Waterside convenience, seasonal',
							},
						],
					},
				},
			],
		},
		{
			title: 'Prepare Your Jet Ski for Storage',
			background: 'dark',
			modules: [
				{
					type: 'videoSection',
					props: {
						content: {
							title: 'Prepare Your Jet Ski for Storage',
							description:
								"Proper preparation is crucial for successful jet ski storage, whether you're planning short-term or winter jet ski storage. Taking time to properly prep your PWC prevents costly repairs and ensures it's ready for your next adventure.",
						},
						src: 'https://www.youtube.com/embed/V_LSnaDLppY',
					},
				},
			],
		},
		{
			title: 'Follow These Steps for Jet Ski Storage Prep',
			modules: [
				{
					type: 'content',
					props: {
						title: 'Follow These Steps for Jet Ski Storage Prep',
						contentType: 'row-type',
						ShowBackgroundImage: true,
						content: [jetSkiStoragePrepMarkdown],
					},
				},
			],
		},
		{
			title: 'Reviews Section',
			background: 'dark',
			modules: [
				{
					type: 'reviews',
					props: {
						reviews,
						title: 'Why SpareFoot for the Best Jet Ski Storage?',
					},
				},
			],
		},
		{
			title: 'FAQ Section',
			modules: [
				{
					type: 'faqs',
					props: {
						header: 'Jet Ski Storage FAQs',
						faqs: jetSkiFaqs,
					},
				},
			],
		},
		{
			title: 'Related Content Section',
			background: 'gray',
			modules: [
				{
					type: 'featuredContent',
					props: {
						title: 'Related Content aka Learn More About',
						contentCards: [
							{
								content:
									'How to Store a Jet Ski During the Off Season',
								link: 'https://sparefoot.com/blog/how-to-store-your-jet-ski',
								title: 'How to Store It',
								image: Featured1,
								Date: 'October 27, 2025',
							},
							{
								content:
									'How to Find The Right Storage Facility For Your Car, RV, or Boat',
								link: 'https://sparefoot.com/blog/how-to-find-the-right-storage-facility-for-your-car-rv-or-boat',
								title: 'General Storage Advice',
								image: Featured2,
								Date: 'October 24, 2025',
							},
							{
								content:
									'Boat Storage Tips: How to Store Your Boat Safely and Efficiently',
								link: 'https://sparefoot.com/blog/storing-a-boat',
								title: 'How to Store It',
								image: Featured3,
								Date: 'September 24, 2025',
							},
						],
					},
				},
			],
		},
		{
			title: 'Link to other PSPs',
			background: 'yellow',
			modules: [
				{
					type: 'links',
					props: {
						title: 'More Ways to Store',
						cards: [
							'Vehicle',
							'Boat',
							'Kayak',
							'RV',
							'Car',
							'DriveUp',
							'24Hour',
							'ClimateControl',
						],
					},
				},
			],
		},
		{
			title: 'nearby units',
			modules: [
				{
					type: 'storageNearYou',
				},
			],
		},
	],
};
